import { contentRequest, office, type ContentFile } from '../../../packages/content-client/src'
import type {EditorPort, InlineEvent} from './editor/EditorPort'

// EditorPort owns transport trust, operation serialization and late-reply rejection.
// The engine frame owns positioning/focus; the host owns authenticated retrieval.
export function createInlineAssistant(file: ContentFile, editor: EditorPort, onIndexed:()=>void=()=>{}) {
  let active = '', sequence = 0, disposed = false
  type Source={origin?:string;memory_id?:string;file_id:string;version:number;section_id:string;content_hash:string}
  let suggestion: { id: string; version: number; memory_context?:{token:string};text: string; citations?:Source[] } | null = null
  const owner=(id:string)=>'inline:'+id
  const reply = (id: string, values: Record<string,unknown>) => { if (!disposed && active === id) editor.inlineResult(id,values) }
  let provider = 'configured provider'
  const configure = () => editor.configureInline({ canWrite: file.can_write, fileName: file.name, provider })
  void contentRequest<{ model_provider?: string }>('/knowledge/office/status').then(status => {
    if (!disposed) { provider = status.model_provider || provider; configure() }
  }).catch(() => { /* Assistance requests report failures; ordinary editing stays usable. */ })
  async function request(values: Record<string, unknown>) {
    const id = values.id
    if (typeof id !== 'string' || id !== active || disposed) return
    const op = values.op, prompt = values.prompt, text = values.text
    const sourceScope=values.scope==='file'?'file':'workspace'
    if (!['complete', 'answer', 'apply'].includes(String(op))) return
    if (op !== 'apply' && (typeof prompt !== 'string' || !prompt.trim() || prompt.length > 500)) return
    const turn = ++sequence
    const current = () => !disposed && active === id && sequence === turn
    try {
      await editor.run(owner(id),String(op),async tx=>{
      if (op === 'apply') {
        if (!suggestion || suggestion.id !== id || typeof text !== 'string' || !text.trim() || text.length > 8000) throw Error('Request a suggestion before inserting text.')
        const latest = await office<ContentFile>('/files/' + file.file_id,{signal:tx.signal})
        if (!current()) return
        if (!latest.can_write || latest.version !== suggestion.version) throw Error('File access or saved version changed. Request a new suggestion.')
        if(suggestion.memory_context){const currentMemory=await contentRequest<{snapshot_token:string}>('/knowledge/office/memory/context?file_id='+encodeURIComponent(file.file_id),{signal:tx.signal});if(currentMemory.snapshot_token!==suggestion.memory_context.token)throw Error('Human knowledge changed. Request a fresh suggestion.')}
        for(const source of suggestion.citations||[]){
          const currentSource=await contentRequest<{current_version:number;content_hash:string}>(source.origin==='human'?`/knowledge/office/memory/${source.memory_id}/validate?revision=${source.version}`:`/knowledge/office/files/${source.file_id}/revisions/${source.version}/sections/${source.section_id}`,{signal:tx.signal})
          if(!current())return
          if(currentSource.current_version!==source.version||currentSource.content_hash!==source.content_hash)throw Error('A referenced source changed. Request a fresh suggestion before inserting.')
        }
        // The frame rechecks its frozen caret/selection before executing paste.
        suggestion = null
        await tx.insertInline(id,text)
      } else if (op === 'complete') {
        suggestion = null
        if (!file.can_write) throw Error('This file is available to you for viewing.')
        if (typeof values.selected !== 'string' || values.selected.length > 4000) throw Error('Select up to 4000 characters and try again.')
        reply(id, { op: 'progress', message: 'Saving to Forge…' })
        await tx.save()
        if (!current()) return
        reply(id, { op: 'progress', message: 'Indexing saved file…' })
        let latest: ContentFile, ready = false
        for (let attempt = 0; attempt < 20; attempt++) {
          if (!current()) return
          latest = await office<ContentFile>('/files/' + file.file_id,{signal:tx.signal})
          if (!latest.can_write) throw Error('Editing access is no longer available.')
          try {
            const graph = await contentRequest<{ version: number }>(`/knowledge/office/files/${file.file_id}/graph`,{signal:tx.signal})
            ready = graph.version === latest.version
          } catch { /* Indexing may still be pending; metadata access is checked on every poll. */ }
          if (ready) break
          await new Promise<void>(resolve => setTimeout(resolve, 750))
        }
        if (!current()) return
        if (!ready) throw Error('The file is saved, but Contextful has not finished indexing it. Try Suggest again shortly.')
        onIndexed()
        reply(id, { op: 'progress', message: 'Writing suggestion…' })
        const result = await contentRequest<{ text: string; version: number;memory_context?:{token:string};citations?:Source[];supported?:boolean }>(`/knowledge/office/files/${file.file_id}/completion`, {
          method: 'POST', signal:tx.signal, body: JSON.stringify({ instruction: prompt, selected_text: values.selected, expected_version: latest.version,source_scope:sourceScope,requested_sources:Array.isArray(values.references)?values.references.slice(0,6):[] }),
        })
        if (!current()) return
        suggestion = result.supported===false?null:{ id, ...result }; reply(id, { op, ...result })
      } else {
        const result = await contentRequest(`/knowledge/office/answer`, { method: 'POST', signal:tx.signal, body: JSON.stringify({ question: prompt, file_id: file.file_id, source_scope:sourceScope,view: 'all', previous_questions: [] }) })
        if (current()) reply(id, { op, result })
      }
      },current)
    } catch (error) { if (current()) reply(id, { op, error: error instanceof Error ? error.message : 'Contextful is unavailable. Try again.' }) }
  }
  function receive(event:InlineEvent) {
    const values=event.values
    if(event.type==='ready')configure()
    if(event.type==='begin'&&typeof values.id==='string'&&values.id.length<=100){
      if(active)editor.cancel(owner(active))
      active=values.id;sequence++;suggestion=null
      const id=active,turn=sequence,current=()=>!disposed&&active===id&&sequence===turn
      if(!editor.idle()){reply(id,{op:'selection',error:'Another editor request is unfinished. Wait, or reopen the file after a selection timeout.'});return}
      if(values.hasSelection===true){
        void editor.run(owner(id),'selection',tx=>tx.copyText(),current)
          .then(selected=>{if(current())reply(id,{op:'selection',selected})})
          .catch(error=>{if(current())reply(id,{op:'selection',error:error.message})})
      }else reply(id,{op:'selection',selected:''})
    }
    if(event.type==='cancel'&&values.id===active){editor.cancel(owner(active));active='';sequence++;suggestion=null}
    if(event.type==='request')void request(values)
  }
  const unsubscribe=editor.onInline(receive)
  return {configure,dispose(){disposed=true;editor.cancel(owner(active));active='';sequence++;suggestion=null;unsubscribe()}}
}
